import { useState } from 'react';
import {
  useAdminJobsGraphQL,
  JobFiltersInput,
  SortDirection,
  DateRange,
} from '@/hooks/graphql/useAdminJobsGraphQL';
import { JobQueueFilters } from './JobQueueFilters';
import { JobQueueTable } from './JobQueueTable';
import { JobDetailModal } from './JobDetailModal';
import logger from '@/utils/logger';

/**
 * Job Queue Inspector Page
 * Feature: 073-job-queue-inspector
 *
 * Admin tool for inspecting pg-boss background jobs.
 * Provides filtering, pagination, and sorting capabilities.
 */
export function JobQueueInspectorPage() {
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [filters, setFilters] = useState<JobFiltersInput>({ dateRange: DateRange.LAST_DAY });
  const [sortBy, setSortBy] = useState('createdOn');
  const [sortOrder, setSortOrder] = useState<SortDirection>('desc');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, loading, error, refetch } = useAdminJobsGraphQL({
    page,
    limit,
    filters,
    sortBy,
    sortOrder,
  });

  const handleFiltersChange = (newFilters: JobFiltersInput) => {
    logger.info('JobQueue', 'Filters changed:', newFilters);
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.adminJobs.hasMore) {
      setPage(page + 1);
    }
  };

  const handleRefresh = () => {
    logger.info('JobQueue', 'Refreshing job list');
    refetch();
  };

  const handleRowClick = (jobId: string) => {
    logger.info('JobQueue', 'Opening job detail modal', jobId);
    setSelectedJobId(jobId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    logger.info('JobQueue', 'Closing job detail modal');
    setIsModalOpen(false);
    setSelectedJobId(null);
  };

  const handleSort = (column: string) => {
    logger.info('JobQueue', 'Sorting by column', column);
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  if (error) {
    logger.error('JobQueue', 'Failed to load jobs:', error);
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">Failed to load jobs</p>
          <p className="text-red-600 text-sm mt-1">{error.message}</p>
          <button
            onClick={handleRefresh}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const jobs = data?.adminJobs.jobs || [];
  const total = data?.adminJobs.total || 0;
  const hasMore = data?.adminJobs.hasMore || false;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-6 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Job Queue Inspector</h1>
              <p className="text-sm text-gray-600 mt-1">
                Inspect background jobs from pg-boss queue
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex gap-4">
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Jobs</p>
            <p className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Current Page</p>
            <p className="text-2xl font-bold text-gray-900">{page + 1}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Per Page</p>
            <p className="text-2xl font-bold text-gray-900">{limit}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <JobQueueFilters filters={filters} onFiltersChange={handleFiltersChange} />
        </div>
      </div>

      {/* Table - Scrollable area */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <JobQueueTable
            jobs={jobs}
            loading={loading}
            onRowClick={handleRowClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      </div>

      {/* Job Detail Modal */}
      <JobDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        jobId={selectedJobId}
      />

      {/* Pagination - Fixed at bottom */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {total > 0 ? (
                  <>
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                  </>
                ) : (
                  'No jobs found'
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={page === 0 || total === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={!hasMore || total === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
